'use client'

import { useStoreI18n, useStoreIntegrations } from '@/store'
import { Typo3SectionSpotSanityTour } from '@/types'
import { Container, Skeleton } from '@/ui/components'
import { OUTDOORACTIVE, PROJECT } from '@spot'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

interface OaDetailpage {
  id?: string
  whenLoaded: (cb: () => void) => void
  destroyASAP?: () => void
  isDestroyed?: () => boolean
}

declare global {
  interface Window {
    oa?: {
      api: {
        detailpage: (config: {
          id: string
          actionOpenType: string
        }) => OaDetailpage
      }
    }
    oax?: {
      _dp?: OaDetailpage
    }
  }
}

// OA keeps the current detailpage instance in a global registry (oax._dp)
// that survives Next's client-side navigations. Re-creating a detailpage
// with the SAME id (same tour remounted: Strict Mode, or list -> same tour)
// throws "instance with same id & dpNode already exists". Destroying an
// instance corrupts OA's flexOpener (map fullscreen dies with "flexOpener:
// wrong page!"), so destroy is a last resort: on a Strict Mode re-run the
// container node is unchanged and the existing instance is reused; only a
// true revisit of the same tour (fresh container) destroys and re-creates.
// Instances of OTHER tours are left alone — OA tolerates them and destroying
// them would break the opener too. destroyASAP() may throw if OA internals
// are already torn down, hence the try/catch.
let lastDp: { id: string; node: Element | null; dp: OaDetailpage } | null = null

const destroyDp = (dp?: OaDetailpage | null) => {
  if (dp && !dp.isDestroyed?.()) {
    try {
      dp.destroyASAP?.()
    } catch {
      // ignore: instance was already gone
    }
  }
}

const MAX_ATTEMPTS = 30
const POLL_INTERVAL = 200

// Loads the OutdoorActive bootstrap on-demand. Skips oa_head.js (which uses
// document.write to redirect to oax/oam_head.js) and goes directly to the
// right variant. Both oax_head.js and oam_head.js *also* use document.write
// internally to load oax/api.js + inline config; calling document.write after
// initial parse wipes the page, so we patch it temporarily, capture the
// writes, then re-inject them as real <script>/<link> elements appended to
// <head> in order.
const ensureOaScript = (lang: string, proj?: string, key?: string) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!proj || !key) {
    return
  }
  if (window.oa?.api) {
    return
  }
  const isMobile = /\b(iPhone|iPad|Android|mobile)\b/i.test(navigator.userAgent)
  const file = isMobile ? 'oam_head.js' : 'oax_head.js'
  if (document.querySelector(`script[data-oa-bootstrap="${file}"]`)) {
    return
  }

  const captured: string[] = []
  const origWrite = document.write
  const origWriteln = document.writeln
  document.write = function patchedWrite(
    this: Document,
    ...args: string[]
  ): void {
    captured.push(args.join(''))
  } as Document['write']
  document.writeln = function patchedWriteln(
    this: Document,
    ...args: string[]
  ): void {
    captured.push(args.join('') + '\n')
  } as Document['writeln']

  const restore = () => {
    document.write = origWrite
    document.writeln = origWriteln
  }

  const replay = () => {
    if (captured.length === 0) {
      return
    }
    const tmp = document.createElement('div')
    tmp.innerHTML = captured.join('')
    const elements = Array.from(tmp.children)
    let idx = 0
    const next = () => {
      if (idx >= elements.length) {
        return
      }
      const el = elements[idx++]
      if (el.tagName === 'SCRIPT') {
        const old = el as HTMLScriptElement
        const s = document.createElement('script')
        if (old.type) {
          s.type = old.type
        }
        if (old.src) {
          s.src = old.src
          s.async = false
          s.onload = next
          s.onerror = next
          document.head.appendChild(s)
        } else {
          s.textContent = old.textContent || ''
          document.head.appendChild(s)
          next()
        }
      } else if (el.tagName === 'LINK' || el.tagName === 'STYLE') {
        document.head.appendChild(el.cloneNode(true))
        next()
      } else {
        next()
      }
    }
    next()
  }

  const script = document.createElement('script')
  script.src = `https://api-oa.com/jscr/${file}?proj=${proj}&key=${key}&lang=${lang}`
  script.async = false
  script.dataset.oaBootstrap = file
  script.onload = () => {
    restore()
    replay()
  }
  script.onerror = restore
  document.head.appendChild(script)
}

const Detail = ({ data }: { data: Typo3SectionSpotSanityTour }) => {
  const baseTourId = data?.content?.item?.tourId
  const backHref =
    data?.content?.uriBack?.href || data?.content?.uriList?.href || ''
  const currentLocale = useStoreI18n.use.currentLocale()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const unmountedRef = useRef(false)

  useEffect(() => {
    unmountedRef.current = false

    const tourId = baseTourId?.toString() ?? ''

    if (!tourId) {
      return
    }

    // Prefer the credentials delivered by TYPO3 (?type=834 integrations),
    // hydrated into the store by Layout's useLayoutEffect (which runs before
    // this effect). Env vars remain as fallback.
    const oaRemote = useStoreIntegrations.getState().integrations?.outdooractive
    const oaProj = oaRemote?.proj || OUTDOORACTIVE?.proj
    const oaKey = oaRemote?.key || OUTDOORACTIVE?.key

    // Plan B: when the TYPO3 backoffice sets BOTH outdooractive values to
    // "external" (?type=834 integrations), the tour is not rendered in-page —
    // the visitor is handed over to surselva.info instead. The same tours
    // are published there under identical slugs and the alle-touren category
    // resolves every tour type (same tab via location.replace, so
    // browser-back returns to the list, not to an empty page). Empty values
    // keep the normal chain: TYPO3 first, env vars as fallback.
    // NOTE: surselva.info is German-only today (other locales 301 to /de),
    // so the target is hardcoded to /de. If surselva.info ever ships further
    // locales (en, ...), build the path from currentLocale instead — and
    // verify the tour slugs match in that locale.
    const isExternal =
      oaRemote?.proj === 'external' && oaRemote?.key === 'external'
    if (isExternal && PROJECT !== 'surselva') {
      const slug = window.location.pathname.split('/').filter(Boolean).pop()
      if (slug) {
        window.location.replace(
          `https://www.surselva.info/de/tourenportal/alle-touren/${slug}`
        )
      }
      return
    }

    ensureOaScript(currentLocale, oaProj, oaKey)

    const wireBackButton = () => {
      if (!backHref) {
        return
      }
      const anchors = document.querySelectorAll<HTMLAnchorElement>(
        '.oax-backlink-detailpage'
      )
      anchors.forEach((a) => {
        if (a.dataset.surselvaBack === '1') {
          return
        }
        a.dataset.surselvaBack = '1'
        a.setAttribute('href', backHref)
        // OA hides the back button when the user arrived directly (no in-app
        // history). We always have a configured list URL, so force-show it.
        a.classList.remove('oax_no_display')
        a.parentElement?.classList.remove('oax_no_display')
        a.addEventListener('click', (e) => {
          e.preventDefault()
          router.push(backHref)
        })
      })
    }

    const waitForOaApi = (attempts = 0): void => {
      if (unmountedRef.current) {
        return
      }
      if (window.oa?.api) {
        const conf = {
          id: tourId,
          actionOpenType: 'in-page'
        }

        const node = document.querySelector('.oax-top-cont')
        let dp: OaDetailpage

        if (
          lastDp &&
          lastDp.id === tourId &&
          lastDp.node === node &&
          !lastDp.dp.isDestroyed?.()
        ) {
          // Strict Mode re-run on the same container: reuse
          dp = lastDp.dp
        } else {
          const stale = window.oax?._dp
          if (stale?.id?.toString() === tourId) {
            destroyDp(stale)
          }
          // instantiate FlexView API detail page
          dp = window.oa.api.detailpage(conf)
          lastDp = { id: tourId, node, dp }
        }

        dp.whenLoaded(() => {
          if (!unmountedRef.current) {
            setIsLoading(false)
            wireBackButton()
          }
        })
      } else if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => waitForOaApi(attempts + 1), POLL_INTERVAL)
      } else {
        if (!unmountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    waitForOaApi()

    // OA re-renders its markup (recreating an unwired, hidden backlink) after
    // in-widget hash navigations (#dm fullscreen, #ipd POI pages). Re-wire on
    // every subtree change; wireBackButton is idempotent via
    // data-surselva-back, and attribute-only mutations (what wiring itself
    // does) don't re-trigger a childList observer.
    const observer = new MutationObserver(() => wireBackButton())
    const topCont = document.querySelector('.oax-top-cont')
    if (topCont) {
      observer.observe(topCont, { childList: true, subtree: true })
    }

    return () => {
      unmountedRef.current = true
      observer.disconnect()
    }
  }, [baseTourId, currentLocale, backHref, router])

  if (!baseTourId) {
    return null
  }

  return (
    <Container className="mt-16">
      <div className="relative">
        {isLoading && (
          <div className="gap-sm relative inset-0 bottom-auto z-10 flex flex-col md:grid md:grid-cols-3">
            <Skeleton className="mt-8 h-10 w-full bg-neutral-200 md:col-span-3" />
            <div className="gap-sm col-span-2 flex flex-col">
              <Skeleton className="h-12 w-full bg-neutral-200" />
              <Skeleton className="h-16 w-full bg-neutral-200" />
            </div>
            <div>
              <Skeleton className="h-full w-full bg-neutral-200" />
            </div>
            <Skeleton className="col-span-2 aspect-16/8 w-full bg-neutral-200" />
            <Skeleton className="h-full w-full bg-neutral-200" />
            <Skeleton className="col-span-2 aspect-16/8 w-full bg-neutral-200" />
            <Skeleton className="h-full w-full bg-neutral-200" />
          </div>
        )}
        <style>
          {`
            .oax.alp-x-lightbox-cont {
              z-index: 9999900 !important;
            }
            .oax .oax_singleView {
             z-index: 1999900 !important;
            }
          `}
        </style>
        <div className="oax-top-cont">
          <div className="oax-part-singleviewpage" />
        </div>
      </div>
    </Container>
  )
}

export default Detail
